/* eslint-disable react/prop-types */
"use client";

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSearchKeyIcon } from "../../constants/searchKeys";

const normalizeRewardAmount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

const formatRewardAmount = (value) => {
  const normalized = normalizeRewardAmount(value);
  if (normalized >= 1e9) {
    const short = (normalized / 1e9).toFixed(1).replace(/\.0$/, "");
    return `${short}B`;
  }
  if (normalized >= 1e6) {
    const short = (normalized / 1e6).toFixed(1).replace(/\.0$/, "");
    return `${short}M`;
  }
  if (normalized >= 1e3) {
    const short = (normalized / 1e3).toFixed(1).replace(/\.0$/, "");
    return `${short}K`;
  }
  return `${normalized}`;
};

const getHostRewardItems = (reward, t) => {
  if (!reward) return [];
  return [
    {
      key: "coins",
      kind: "coins",
      amount: normalizeRewardAmount(reward.coins),
      amountLabel: formatRewardAmount(reward.coins),
      icon: "/assets/vault/coin_icon.svg",
      label: t("participants_section.host_reward_items.coins"),
      iconClassName: "h-[1em] w-[1em]",
    },
    {
      key: "diamonds",
      kind: "resource",
      amount: normalizeRewardAmount(reward.diamonds),
      amountLabel: formatRewardAmount(reward.diamonds),
      icon: "/assets/vault/diamand_icon.svg",
      label: t("participants_section.host_reward_items.diamonds"),
      iconClassName: "h-6 w-6",
    },
    {
      key: "key1",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_1),
      amountLabel: formatRewardAmount(reward.keys_level_1),
      icon: getSearchKeyIcon(1),
      label: t("participants_section.host_reward_items.key_level_1"),
      iconClassName: "h-10 w-10 rotate-[45deg]",
    },
    {
      key: "key2",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_2),
      amountLabel: formatRewardAmount(reward.keys_level_2),
      icon: getSearchKeyIcon(2),
      label: t("participants_section.host_reward_items.key_level_2"),
      iconClassName: "h-10 w-10 rotate-[45deg]",
    },
    {
      key: "key3",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_3),
      amountLabel: formatRewardAmount(reward.keys_level_3),
      icon: getSearchKeyIcon(3),
      label: t("participants_section.host_reward_items.key_level_3"),
      iconClassName: "h-10 w-10 rotate-[45deg]",
    },
    {
      key: "key4",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_4),
      amountLabel: formatRewardAmount(reward.keys_level_4),
      icon: getSearchKeyIcon(4),
      label: t("participants_section.host_reward_items.key_level_4"),
      iconClassName: "h-10 w-10 rotate-[45deg]",
    },
    {
      key: "key5",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_5),
      amountLabel: formatRewardAmount(reward.keys_level_5),
      icon: getSearchKeyIcon(5),
      label: t("participants_section.host_reward_items.key_level_5"),
      iconClassName: "h-10 w-10 rotate-[45deg]",
    },
  ].filter((item) => item.amount > 0);
};

export default function HostRewardBlock({
  reward = null,
  align = "left",
  className = "",
  titleClassName = "",
  showHintToggle = true,
}) {
  const { t } = useTranslation("events");
  const [showRewardHint, setShowRewardHint] = useState(false);

  const hostRewardItems = useMemo(
    () => getHostRewardItems(reward, t),
    [reward, t]
  );

  if (!hostRewardItems.length) return null;

  const centered = align === "center";

  return (
    <div className={className}>
      <div className={`flex items-center gap-2 ${centered ? "justify-center" : "justify-start"}`}>
        <span
          className={
            titleClassName ||
            `${centered ? "text-xl font-bold" : "text-2xl font-extrabold leading-6"} text-white`
          }
        >
          {t("participants_section.host_reward_title")}
        </span>
        {showHintToggle ? (
          <button
            type="button"
            onClick={() => setShowRewardHint((prev) => !prev)}
            className="relative"
            aria-label={t("participants_section.host_reward_help_aria")}
          >
            <span className="absolute inset-0 rounded-md bg-white/20" aria-hidden="true" />
            <img
              src="/assets/vault/question_icon.svg"
              alt="?"
              className="relative z-[1] m-[3px] h-4 w-4"
            />
          </button>
        ) : null}
      </div>

      {showHintToggle && showRewardHint ? (
        <p
          className={`mt-1 max-w-[560px] text-xs leading-relaxed text-white/80 ${
            centered ? "text-center mx-auto" : "text-left"
          }`}
        >
          {t("participants_section.host_reward_hint")}
        </p>
      ) : null}

      <div
        className={`mt-1.5 flex flex-wrap items-center ${
          centered ? "justify-center" : "justify-start"
        } gap-x-3 gap-y-1.5`}
      >
        {hostRewardItems.map((item) => (
          item.kind === "coins" ? (
            <div
              key={item.key}
              className={`flex items-center gap-0.5 text-white ${
                centered ? "text-lg" : "text-xl"
              }`}
            >
              <span className="font-extrabold leading-none">
                {item.amountLabel}
              </span>
              <img
                src={item.icon}
                alt={item.label}
                className={`${item.iconClassName} shrink-0 object-contain`}
              />
            </div>
          ) : (
            <div key={item.key} className={`flex items-center text-white ${item.key === "diamonds" ? "gap-1.5" : "gap-0"}`}>
              <img src={item.icon} alt={item.label} className={`${item.iconClassName} shrink-0 object-contain`} />
              <span className={`${centered ? "text-lg" : "text-xl"} ${item.key === "diamonds" ? "" : "-ml-2.5"} whitespace-nowrap font-extrabold leading-none`}>
                × {item.amountLabel}
              </span>
            </div>
          )
        ))}
      </div>
    </div>
  );
}
